import type { Activity } from '@/features/project-board/analytics/trend';

const WIDTH = 900;
const HEIGHT = 230;
const PAD = { top: 14, right: 44, bottom: 24, left: 40 };
const PLOT_W = WIDTH - PAD.left - PAD.right;
const PLOT_H = HEIGHT - PAD.top - PAD.bottom;

/**
 * 활동 추세 — 누적 task 완료(왼쪽 축, 선)와 주간 에픽·이슈 이벤트(오른쪽 축, 막대)를 같은 시간축에 겹친다.
 */
export function ActivityChart({ data }: { data: Activity }): React.JSX.Element {
  const points = data.points;
  const leftMax = Math.max(data.scope, ...points.map((point) => point.cumulative), 1);
  const rightMax = Math.max(1, ...points.flatMap((point) => [point.epicUpdates, point.opened, point.closed]));

  const x = (index: number) => PAD.left + (index / Math.max(1, points.length - 1)) * PLOT_W;
  const yLeft = (value: number) => PAD.top + PLOT_H - (value / leftMax) * PLOT_H;
  const yRight = (value: number) => PAD.top + PLOT_H - (value / rightMax) * PLOT_H;

  const slot = PLOT_W / Math.max(1, points.length - 1);
  const barWidth = Math.min(7, slot * 0.24);

  const line = points.map((point, index) => `${index === 0 ? 'M' : 'L'}${x(index)},${yLeft(point.cumulative)}`).join(' ');

  return (
    <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="h-56 w-full" role="img" aria-label="task 완료, 에픽 문서 갱신, 이슈 접수·처리 추세">
      {[0, Math.round(leftMax / 2), leftMax].map((tick) => (
        <g key={`l-${tick}`}>
          <line x1={PAD.left} x2={WIDTH - PAD.right} y1={yLeft(tick)} y2={yLeft(tick)} className="stroke-line-soft" />
          <text x={PAD.left - 6} y={yLeft(tick) + 3} textAnchor="end" className="fill-done text-[9px]">
            {tick}
          </text>
        </g>
      ))}

      {[0, rightMax].map((tick) => (
        <text
          key={`r-${tick}`}
          x={WIDTH - PAD.right + 6}
          y={yRight(tick) + 3}
          className="fill-progress text-[9px]"
        >
          {tick}
        </text>
      ))}

      {/* 전체 스코프 기준선 — 누적선과의 간격이 남은 일이다. */}
      <line
        x1={PAD.left}
        x2={WIDTH - PAD.right}
        y1={yLeft(data.scope)}
        y2={yLeft(data.scope)}
        className="stroke-muted"
        strokeDasharray="5 4"
      />
      <text x={WIDTH - PAD.right} y={yLeft(data.scope) - 4} textAnchor="end" className="fill-muted text-[9px]">
        전체 {data.scope}
      </text>

      {/* 주간 이벤트 막대(오른쪽 축). 선보다 뒤에 그려 배경처럼 둔다. */}
      {points.map((point, index) => (
        <g key={`bars-${point.start}`}>
          <rect
            x={x(index) - barWidth - 1}
            y={yRight(point.epicUpdates)}
            width={barWidth}
            height={PAD.top + PLOT_H - yRight(point.epicUpdates)}
            rx={1.5}
            className="fill-accent/60"
          >
            <title>{`${point.start} · 에픽 문서 갱신 ${point.epicUpdates}건`}</title>
          </rect>
          <rect
            x={x(index)}
            y={yRight(point.opened)}
            width={barWidth}
            height={PAD.top + PLOT_H - yRight(point.opened)}
            rx={1.5}
            className="fill-progress/45"
          >
            <title>{`${point.start} · 이슈 접수 ${point.opened}건`}</title>
          </rect>
          <rect
            x={x(index) + barWidth + 1}
            y={yRight(point.closed)}
            width={barWidth}
            height={PAD.top + PLOT_H - yRight(point.closed)}
            rx={1.5}
            className="fill-done/35"
          >
            <title>{`${point.start} · 이슈 처리 ${point.closed}건`}</title>
          </rect>
        </g>
      ))}

      <path d={line} fill="none" strokeWidth={2} className="stroke-done" />
      {points.map((point, index) => (
        <g key={`dot-${point.start}`}>
          <circle cx={x(index)} cy={yLeft(point.cumulative)} r={2.6} className="fill-done">
            <title>{`${point.start} · 누적 완료 ${point.cumulative} (+${point.done})`}</title>
          </circle>
          {index % 2 === 0 && (
            <text x={x(index)} y={HEIGHT - 8} textAnchor="middle" className="fill-faint text-[9px]">
              {point.label}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
}
